// Loads the inventory changelog (material cost entries) for the
// workspace org. Backed by /api/mfg/cost-entries; standard
// mfg-addon gate, no admin gating (the ledger is operational, not
// audit). Mirrors `useManufacturingAuditLog` in shape so anyone
// reading both side-by-side sees the same loading/working/error
// machinery.
import { useCallback, useEffect, useState } from "react";
import { listManufacturingInventoryChangelog } from "@/lib/manufacturingClient";
import type {
  MfgInventoryChangelogFilters,
  MfgInventoryChangelogRow,
} from "@/lib/manufacturing/types";

export type UseManufacturingInventoryChangelogResult = {
  entries: MfgInventoryChangelogRow[];
  total: number;
  loading: boolean;
  working: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useManufacturingInventoryChangelog(opts: {
  enabled: boolean;
  filters?: MfgInventoryChangelogFilters;
}): UseManufacturingInventoryChangelogResult {
  const { enabled } = opts;
  const filters = opts.filters;
  const [entries, setEntries] = useState<MfgInventoryChangelogRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [working, setWorking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setWorking(true);
    setError(null);
    const r = await listManufacturingInventoryChangelog(filters ?? {});
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      setEntries([]);
      setTotal(0);
      return;
    }
    setEntries(r.data.entries);
    setTotal(r.data.total);
  }, [enabled, filters]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setEntries([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    void (async () => {
      const r = await listManufacturingInventoryChangelog(filters ?? {});
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.error);
        setEntries([]);
        setTotal(0);
        return;
      }
      setEntries(r.data.entries);
      setTotal(r.data.total);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, filters]);

  return { entries, total, loading, working, error, refresh };
}
